/**
 * The ticket providers TixInfo works with — one list for the build (scripts/), the edge
 * (functions/), the background jobs (workers/) and the site (src/).
 *
 * A provider joining, leaving or changing its terms is an edit here, plus its own data module
 * (docs/ARCHITECTURE.md, "Adding a ticket provider"). Everything else — which link field an
 * event carries, whether its prices include fees, which hosts /go/ may send a visitor to, the
 * pull-health check, logos — is read from this list.
 *
 * PUBLIC FACTS ONLY: this file is bundled into the browser. Affiliate ids and tracking
 * wrappers stay on the edge (functions/go/[market].ts) and are added there for human clicks.
 *
 *   id      stable key: the /go/<id> click route, logo lookups, code
 *   name    display name — also the `source` of every offer and price point. Stored in D1 and
 *           in the price history: renaming a provider is a data migration, not an edit.
 *   field   the event's exact-link field the build writes (htUrl, tsUrl …)
 *   role    'partner'   priced rows with our affiliate link, on every event they list
 *           'discovery' finds events (the radar); no row until a contract is live
 *           'catalog'   SeatGeek — the spine of the prerendered catalog; no row
 *   allIn   whether its listed prices include fees (true) or fees come at checkout (false).
 *           An offer's own reading wins (a live widget can say otherwise): isAllIn().
 *   pull    its key in the pull-health counts (scripts/.edge-sources.json): a short pull
 *           holds back retiring pages (scripts/push-events.mjs)
 *   hosts   where /go/<id> may send a visitor
 *   logo    file in public/partners/, null = wordmark
 *   live    the edge can refresh its price for a visitor (functions/api/prices.ts)
 *   feeWedge  sticker -> with-fees multiplier, measured, for RANKING only (rankPrice); absent = 1.25
 *   exchange  the inventory exchange it resells (Ticket Squeeze and TicketNetwork list the same seats
 *             from one exchange): one offer's widget reading outranks the other's feed minimum
 */

/**
 * @typedef {{ id: string, name: string, field: string, role: 'partner' | 'discovery' | 'catalog',
 *   allIn: boolean, pull: string | null, hosts: string[], logo: string | null, color: string | null, live: boolean, feeWedge?: number, exchange?: string }} Provider
 */

/** @type {Provider[]} */
export const PROVIDERS = [
  {
    id: 'hellotickets',
    name: 'HelloTickets',
    field: 'htUrl',
    role: 'partner',
    // US storefront prices are all-in: the page total equals the API price (verified 2026-07-14)
    allIn: true,
    pull: 'ht',
    hosts: ['www.hellotickets.com', 'hellotickets.com'],
    logo: 'hellotickets.svg',
    color: '#526FD1',
    live: true,
  },
  {
    id: 'ticketsqueeze',
    name: 'Ticket Squeeze',
    field: 'tsUrl',
    role: 'partner',
    // feed prices are sticker prices (a real checkout, 2026-07-15); a widget reading is all-in
    // and says so on its own offer. See src/data/marketplaces.ts before changing this.
    allIn: false,
    // TS's own site asks a median 1.076x its feed price for the same event and day (IQR
    // 1.072-1.083, n = 2,139; D1 price_point, 2026-10-14), so its fees add ~8%, not the 25%
    // the board assumed, which ranked TicketNetwork first on 59% of shared boards although
    // TicketNetwork prices the same seats ~13% higher all-in.
    feeWedge: 1.08,
    exchange: 'seatics',
    pull: 'ts',
    hosts: ['www.ticketsqueeze.com', 'ticketsqueeze.com'],
    logo: null,
    color: null,
    live: false,
  },
  {
    id: 'ticketnetwork',
    name: 'TicketNetwork',
    field: 'tnUrl',
    role: 'partner',
    // the Impact feed minimum matched TicketNetwork's fees-included widget to the cent (2026-08-25)
    allIn: true,
    pull: 'tn',
    exchange: 'seatics',
    hosts: ['goto.ticketnetwork.com', 'www.ticketnetwork.com', 'ticketnetwork.com'],
    logo: 'ticketnetwork.svg',
    color: null,
    live: false,
  },
  {
    id: 'ticketmaster',
    name: 'Ticketmaster',
    field: 'tmUrl',
    role: 'discovery',
    allIn: false,
    pull: null,
    hosts: ['www.ticketmaster.com', 'ticketmaster.com'],
    logo: null,
    color: null,
    live: false,
  },
  {
    id: 'seatgeek',
    name: 'SeatGeek',
    field: 'sgUrl',
    role: 'catalog',
    allIn: false,
    pull: 'pool',
    hosts: ['seatgeek.com', 'www.seatgeek.com'],
    logo: null,
    color: null,
    live: false,
  },
];

/** @type {Map<string, Provider>} */
const BY_ID = new Map(PROVIDERS.map((p) => [p.id, p]));
/** @type {Map<string, Provider>} */
const BY_NAME = new Map(PROVIDERS.map((p) => [p.name, p]));

/** @param {string | null | undefined} id */
export const providerById = (id) => (id ? BY_ID.get(id) ?? null : null);
/** @param {string | null | undefined} name */
export const providerByName = (name) => (name ? BY_NAME.get(name) ?? null : null);

/** The providers with priced, linked rows today. */
export const PARTNERS = PROVIDERS.filter((p) => p.role === 'partner');

/** The pull-health keys the push checks before retiring anything. */
export const PULL_KEYS = PROVIDERS.map((p) => p.pull).filter((k) => k !== null);

/**
 * Does this offer's price include fees? Its own reading wins (a live widget reading); else
 * its provider's rule. Every fee label and every "cheapest all-in" decision asks this.
 * @param {{ source?: string, allIn?: boolean } | null | undefined} offer
 */
export const isAllIn = (offer) => (typeof offer?.allIn === 'boolean' ? offer.allIn : (providerByName(offer?.source)?.allIn ?? false));

/** What fees add to a marketplace's sticker price, when its prices are sticker: measured where we
 *  have it (Provider.feeWedge), otherwise the cautious 25% the board always assumed. */
export const FEE_WEDGE = 1.25;
/** @param {string | null | undefined} source */
export const feeWedge = (source) => providerByName(source)?.feeWedge ?? FEE_WEDGE;

/**
 * The number the board ranks an offer by: its own price when fees are included, a sticker price
 * times its marketplace's fee wedge. "Cheapest first" then means the cheapest total, so a sticker
 * price neither outranks a cheaper total nor loses to a dearer one. An ESTIMATE: it is shown only
 * where it is said to be one ("about $154 with fees, est."), never as a price, and never in a
 * title, a snippet, structured data, a card or the buy bar (one-best-offer, 2026-10-22).
 * @param {{ source?: string, allIn?: boolean, from: number }} offer
 */
export const rankPrice = (offer) => (isAllIn(offer) ? offer.from : offer.from * feeWedge(offer.source));

/**
 * PHANTOM FLOORS (2026-10-14). A before-fees price under half the cheapest fees-included price
 * for the same event is a stale or mis-read listing, not a bargain: Rod Wave Chicago headlined
 * "$32.29 + fees … 77% below the 30-day median" while the same exchange listing was $141.45
 * all-in on TicketNetwork, and 1,264 events headlined such a price. Fees on a sticker price are
 * ~8-25%, never 100%. Only sticker prices are tested: an all-in price from a marketplace with
 * its own inventory (HelloTickets) can genuinely be far cheaper.
 */
export const SUSPECT_RATIO = 0.5;

/**
 * Marks each sticker offer priced under SUSPECT_RATIO x the cheapest fees-included offer of the
 * same event (`suspect: true`; clears a stale mark). Mutates and returns the list.
 * @template {{ source?: string, allIn?: boolean, from: number, suspect?: boolean }} O
 * @param {O[] | null | undefined} offers
 */
export const markSuspects = (offers) => {
  const list = offers ?? [];
  const live = (/** @type {any} */ o) => o.from >= PLACEHOLDER_MIN && !o.placeholder && !o.stale;
  // OUTLIER LOW (re-review, 2026-10-22): an offer under OUTLIER_LOW_RATIO of the cheapest OTHER usable
  // offer, which is itself at least OUTLIER_LOW_OTHER, is no price: HelloTickets sends "$2" for some
  // events against TicketNetwork's $102-$294 (four indexed Citizens Live at The Wylie pages headlined
  // "from $2"; TicketNetwork's own widget has no group under $294). Real cheap seats are untouched:
  // Illinois at Maryland reads 3 / 3 / 3.45, nobody is 5% of anybody.
  const outlier = new Set();
  for (const o of list) {
    if (!(o.from > 0)) continue;
    const others = list.filter((x) => x !== o && live(x)).map((x) => x.from);
    if (others.length && Math.min(...others) >= OUTLIER_LOW_OTHER && o.from < Math.min(...others) * OUTLIER_LOW_RATIO) outlier.add(o);
  }
  // the sticker reference is a fees-included price a headline could quote: a placeholder, a stale offer,
  // a sub-cent listing or an outlier-low is no reference (a real $139 sticker was "suspect" against a
  // $981 stand-in)
  const allIn = list.filter((o) => live(o) && isAllIn(o) && !outlier.has(o)).map((o) => o.from);
  const floor = allIn.length ? Math.min(...allIn) : null;
  for (const o of list) {
    if (outlier.has(o) || (floor !== null && o.from > 0 && !isAllIn(o) && o.from < floor * SUSPECT_RATIO)) o.suspect = true;
    else delete o.suspect;
  }
  return offers;
};

/** an offer under this share of the cheapest other usable offer ... */
export const OUTLIER_LOW_RATIO = 0.05;
/** ... when that other offer is at least this much, is a suspect price */
export const OUTLIER_LOW_OTHER = 50;

/**
 * ONE BEST OFFER (2026-10-22). The title, the snippet, the structured data, the hero, the cards,
 * the buy bar, the FAQ and the verdict each took "the cheapest" their own way: the cheapest raw
 * number (often a Ticket Squeeze price before fees) against a board that ranks by estimated total,
 * so 12,663 of 172,973 live priced events headlined a price that was not the board's first row, and
 * Bears-Lions read "From $174 · + fees at checkout" over a $174 that includes fees. Everything now
 * asks `bestOffer` / `rankOffers`, and takes the price AND its fee label from the same offer.
 *
 * An offer carries these flags, written by the build or the edge (never by the page):
 *   suspect      markSuspects: a before-fees price far under every fees-included one
 *   placeholder  markPlaceholders: a broker's stand-in price, not a real listing
 *   stale        markStale: its `seen` day is more than FRESH_DAYS old
 *   seen         the last day a partner pull still held this offer (YYYY-MM-DD); absent = today's pull
 *   qty          what `from` is the floor OF: 2 = the cheapest group of two or more tickets (a widget
 *                reading, scripts/gen-catalog.mjs), 1 = the widget found single-seat groups only,
 *                absent = unknown (a feed minimum, which is often one seat; HelloTickets' minimum)
 *   from1        the cheapest single-seat group, when it is cheaper than `from` (qty 2 readings)
 */

/**
 * PRICES FOR TWO TICKETS ARE OFF (coordinator, 2026-10-22; the review of w1/clean-price-history found
 * 7 of 30 problems in them: single seat -> pair steps read as price moves, groups of 3, HelloTickets'
 * minimum is not quantity-aware). While `pairFloors` is false, a headline, the board's order and
 * claim, and the history are the floor PER TICKET exactly as before: the Seatics parser still reads
 * the quantity (scripts/lib/seatics.mjs) but gen-catalog writes no `qty`/`from1` on an offer, and
 * the `qty` rules below (single-seat tier, exchange hold, 4th tape slot) are dormant. Wave 2 (party
 * size prices, W2-5) builds the real thing; a test flips the flag to keep the dormant rules honest.
 */
export const PRICE_FLAGS = { pairFloors: false };

/** a listed price below this is no price at all (cents); the long-tail feed has its own $5 floor at
 *  intake (scripts/gen-longtail.mjs MIN_PRICE): real $2-$4 seats exist (Illinois at Maryland, $3) */
export const PLACEHOLDER_MIN = 1;
/** an offer not seen in a partner pull for more than this many days is stale */
export const FRESH_DAYS = 3;
/** estimated totals this close are a tie; a fees-included price then outranks a sticker price */
export const TIE_RATIO = 0.01;

const MS_DAY = 864e5;
const daysApart = (a, b) => Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / MS_DAY);

/**
 * Marks each offer whose `seen` day is more than FRESH_DAYS before `today` (`stale: true`; clears a
 * stale mark). An offer with no `seen` is from today's pull. Mutates and returns the list; returns
 * it untouched without a valid `today`. @template {{ seen?: string, stale?: boolean }} O
 * @param {O[] | null | undefined} offers @param {string} [today] YYYY-MM-DD
 */
export const markStale = (offers, today) => {
  if (!today || !/^\d{4}-\d{2}-\d{2}$/.test(today)) return offers;
  for (const o of offers ?? []) {
    if (typeof o.seen === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(o.seen) && daysApart(o.seen, today) > FRESH_DAYS) o.stale = true;
    else delete o.stale;
  }
  return offers;
};

/** @typedef {{ source?: string, allIn?: boolean, from: number, suspect?: boolean, placeholder?: boolean, stale?: boolean, qty?: number, seen?: string }} RankedOffer */

/** An offer a headline may quote: priced, not a stand-in, not suspect, not stale. @param {RankedOffer | null | undefined} o */
export const usableOffer = (o) => !!o && o.from >= PLACEHOLDER_MIN && !o.suspect && !o.placeholder && !o.stale;

/** @param {RankedOffer} o */
const tier = (o) => (usableOffer(o) ? (PRICE_FLAGS.pairFloors && o.qty === 1 ? 1 : 0) : 2);

/** @param {string | undefined} source */
const exchangeOf = (source) => providerByName(source)?.exchange ?? null;

/**
 * The one order: priced offers (from > 0); usable ones first, those whose floor is a single seat
 * after the rest (the board's default is two tickets), unusable ones (suspect, placeholder, stale)
 * last; inside a group the cheapest estimated total (rankPrice) first, a fees-included price
 * winning when two totals are within TIE_RATIO, then the marketplace's name. An offer on the
 * exchange that has no widget reading today (qty absent) never outranks a read offer of the same
 * exchange: its feed minimum is usually one seat (TicketNetwork's $200 single seat against Ticket
 * Squeeze's $411 pair on KATSEYE). `held` are those offers. A new array; the inputs are untouched.
 * @template {RankedOffer} O
 * @param {O[] | null | undefined} offers
 * @returns {{ order: O[], held: Set<O> }}
 */
const rankInner = (offers) => {
  const order = (offers ?? [])
    .filter((o) => o.from > 0)
    .sort((a, b) => tier(a) - tier(b) || rankPrice(a) - rankPrice(b) || String(a.source ?? '').localeCompare(String(b.source ?? '')));
  // a fees-included offer within TIE_RATIO of the sticker offer ahead of it moves up
  for (let again = true, guard = 0; again && guard < 50; guard++) {
    again = false;
    for (let i = 0; i + 1 < order.length; i++) {
      const a = order[i];
      const b = order[i + 1];
      if (tier(a) === tier(b) && !isAllIn(a) && isAllIn(b) && rankPrice(b) <= rankPrice(a) * (1 + TIE_RATIO)) {
        order[i] = b;
        order[i + 1] = a;
        again = true;
      }
    }
  }
  /** @type {Set<O>} */
  const held = new Set();
  for (const u of [...order]) {
    const ex = exchangeOf(u.source);
    if (!PRICE_FLAGS.pairFloors || !ex || typeof u.qty === 'number' || !usableOffer(u)) continue;
    const read = order.filter((r) => r !== u && exchangeOf(r.source) === ex && typeof r.qty === 'number' && usableOffer(r));
    if (!read.length) continue;
    const last = Math.max(...read.map((r) => order.indexOf(r)));
    const at = order.indexOf(u);
    if (at < last) {
      order.splice(at, 1);
      order.splice(last, 0, u); // after the last read offer it would have outranked
      held.add(u);
    }
  }
  return { order, held };
};

/**
 * The board's order, the one rule the buy bar, the FAQ and the cards use (see rankInner).
 * @template {RankedOffer} O
 * @param {O[] | null | undefined} offers
 * @returns {O[]}
 */
export const rankOffers = (offers) => rankInner(offers).order;

/** The board's order. @template {RankedOffer} O @param {O[] | null | undefined} offers @returns {O[]} */
export const boardOrder = (offers) => rankOffers(offers);

/**
 * THE offer an event quotes — title, snippet, structured data, hero, cards, buy bar, FAQ, verdict:
 * the first usable offer of the board's order, or null. Never a suspect, placeholder or stale offer,
 * and never a fallback to one: an event whose offers are all unusable has no headline price.
 * @template {RankedOffer} O
 * @param {O[] | null | undefined} offers
 * @returns {O | null}
 */
export const bestOffer = (offers) => rankOffers(offers).find((o) => usableOffer(o)) ?? null;

/** `headlineOffer` is the old name of `bestOffer`: both builds and the page switch at once. */
export const headlineOffer = bestOffer;

/** the fields a headline price writes on an event (and clears together) */
const FLOOR_KEYS = ['lowestPrice', 'priceSource', 'priceUrl', 'lowestAllIn', 'mixedBasis'];

/**
 * Writes an event's headline fields from its best offer — THE place both builds and the edge do it
 * (scripts/gen-catalog.mjs, scripts/gen-longtail.mjs, functions/_lib/edge-page.ts): fee basis
 * written down on every offer, suspects and (given `today`) stale offers marked, then bestOffer.
 * With no usable offer the headline fields are cleared, never left as they were. Returns the best
 * offer. `listingCount` (the catalog's listing depth) is written only when asked for.
 * @template {{ offers?: any[], listingCount?: number | null }} E
 * @param {E & Record<string, any>} e @param {{ today?: string, listingCount?: boolean }} [opts]
 */
export const applyFloor = (e, opts = {}) => {
  withFeeBasis(e.offers);
  // stale first: a stale offer is no reference for a suspect price (placeholders are marked by the build before this)
  markStale(e.offers, opts.today);
  markSuspects(e.offers);
  const best = bestOffer(e.offers);
  if (!best) {
    for (const k of FLOOR_KEYS) delete e[k];
    return null;
  }
  e.lowestPrice = best.from;
  e.priceSource = best.source;
  e.priceUrl = best.url;
  if (opts.listingCount && best.listings) e.listingCount = best.listings;
  // FEE BASIS OF THE HEADLINE NUMBER, from the very offer that sets the number: cards, SERP titles
  // and the hero all quote lowestPrice, and none may say "fees included" about a price before fees.
  e.lowestAllIn = isAllIn(best);
  // Do the offers a visitor can use share a fee basis? When they do not, "cheapest first" is not a
  // like-for-like ranking and the page says so.
  e.mixedBasis = new Set((e.offers ?? []).filter(usableOffer).map((o) => isAllIn(o))).size > 1;
  return best;
};



/** a price at least this high may be a stand-in (a $46 price on 2,796 events is a real price point) */
export const PLACEHOLDER_FLOOR = 500;
/** the same price, to the cent, on this many events ... */
export const PLACEHOLDER_REPEAT = 25;
/** ... at this many venues is no listing */
export const PLACEHOLDER_VENUES = 10;
/** a lone offer this many times its act's other dates' median is no listing */
export const PLACEHOLDER_MULT = 8;
/** a price quoted to the CENT (not a whole-dollar one) that repeats on this many events ... */
export const STUB_EVENTS = 3;
/** ... at this many venues and this many different acts is a feed's stand-in, not a coincidence (2935.82 on Viking FK x3 and the Mexico F1 Saturday) */
export const STUB_VENUES = 2;
export const STUB_ACTS = 2;
/** is this amount quoted with cents? (a whole-dollar price repeats by chance: $598 on four Sphere dates) */
export const hasCents = (/** @type {number} */ n) => Math.abs(n * 100 - Math.round(n) * 100) > 0.5;

/**
 * PLACEHOLDER PRICES (offer-guard-v2 B1, 2026-10-22). Brokers list a stand-in price on events whose
 * seats are not released: $999.14 on 223 events, $981 on 334, $1,061.59 on 157 (1,099 live events in
 * all, ~940 headlining it). Its TicketNetwork twin is the exchange price x 1.22 ($1,219). A price is
 * a placeholder when
 *  - it is at least PLACEHOLDER_FLOOR and the same price to the cent appears on at least
 *    PLACEHOLDER_REPEAT events at PLACEHOLDER_VENUES or more venues IN THE SAME MARKETPLACE's own
 *    offers (a coincidence of another marketplace's price never flags a real one: HelloTickets'
 *    $575 for the Sphere was called a stand-in because Ticket Squeeze has $575.00 on 67 events); the
 *    counts come from the events given plus `opts.counts` (the whole feeds, per marketplace);
 *  - it is the twin of a placeholder on the same event on the other side of the exchange (Ticket
 *    Squeeze x 1.22 within $0.10 gives TicketNetwork; either way within 1% of the measured 1.22 or
 *    of the 1.138 storefront premium, which catches the Ticket Squeeze widget reading of a flagged
 *    feed price); or
 *  - it is the event's only priced offer, at least PLACEHOLDER_FLOOR, and PLACEHOLDER_MULT times
 *    the median of the SAME ACT's other dates (3 or more of them): never a venue's or category's.
 * Writes `placeholder: true` on those offers and clears a stale mark. Nothing is ever deleted from
 * the history on its say-so. Returns counts per rule.
 * @param {{ id?: string, artist?: string, venue?: string, offers?: { source?: string, from: number, placeholder?: boolean }[] }[]} events
 * @param {{ counts?: Map<string, { events: number, venues: number, acts?: number }> }} [opts] keyed by `source|cents` (placeholderCounts)
 */
export function markPlaceholders(events, opts = {}) {
  const key = (/** @type {string | undefined} */ source, /** @type {number} */ n) => `${source ?? ''}|${Math.round(n * 100)}`;
  /** @type {Map<string, { ev: Set<string>, venues: Set<string>, acts: Set<string> }>} */
  const seen = new Map();
  events.forEach((e, i) => {
    for (const o of e.offers ?? []) {
      if (!(o.from >= PLACEHOLDER_FLOOR)) continue;
      const k = key(o.source, o.from);
      const b = seen.get(k) ?? { ev: new Set(), venues: new Set(), acts: new Set() };
      b.ev.add(e.id ?? String(i));
      b.venues.add(e.venue ?? '');
      b.acts.add(String(e.artist || e.name || e.id || i).toLowerCase());
      seen.set(k, b);
    }
  });
  const repeated = (/** @type {string | undefined} */ source, /** @type {number} */ price) => {
    const k = key(source, price);
    const own = seen.get(k);
    const ext = opts.counts?.get(k);
    if (Math.max(own?.ev.size ?? 0, ext?.events ?? 0) >= PLACEHOLDER_REPEAT && Math.max(own?.venues.size ?? 0, ext?.venues ?? 0) >= PLACEHOLDER_VENUES) return true;
    // the same cent-exact amount on a few unrelated events: a feed default, whatever the count of the whole feed
    const extActs = ext?.acts ?? 0;
    return (
      hasCents(price) &&
      Math.max(own?.ev.size ?? 0, ext?.events ?? 0) >= STUB_EVENTS &&
      Math.max(own?.venues.size ?? 0, ext?.venues ?? 0) >= STUB_VENUES &&
      Math.max(own?.acts.size ?? 0, extActs) >= STUB_ACTS
    );
  };
  // each act's cheapest price per date, for the outlier rule
  /** @type {Map<string, { i: number, price: number }[]>} */
  const byAct = new Map();
  events.forEach((e, i) => {
    const prices = (e.offers ?? []).filter((o) => o.from > 0).map((o) => o.from);
    if (!e.artist || !prices.length) return;
    const list = byAct.get(e.artist) ?? [];
    list.push({ i, price: Math.min(...prices) });
    byAct.set(e.artist, list);
  });
  const median = (/** @type {number[]} */ xs) => {
    const s = [...xs].sort((a, b) => a - b);
    return s.length % 2 ? s[(s.length - 1) / 2] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2;
  };
  /** is `o` (the other side of the exchange) the twin of placeholder `p`? TN / TS is 1.22 (feed against feed, ±$0.10) or the 1.138 premium (±1%) */
  const twin = (/** @type {{ source?: string, from: number }} */ p, /** @type {{ source?: string, from: number }} */ o) => {
    const pair = p.source === 'Ticket Squeeze' && o.source === 'TicketNetwork' ? o.from / p.from : p.source === 'TicketNetwork' && o.source === 'Ticket Squeeze' ? p.from / o.from : null;
    if (pair === null) return false;
    if (p.source === 'Ticket Squeeze' && Math.abs(o.from - p.from * 1.22) <= 0.1) return true;
    return Math.abs(pair - 1.22) <= 0.0122 || Math.abs(pair - 1.138) <= 0.01138;
  };
  const by = { repeat: 0, twin: 0, outlier: 0 };
  events.forEach((e, i) => {
    const offers = e.offers ?? [];
    for (const o of offers) delete o.placeholder;
    for (const o of offers) {
      if (o.from >= PLACEHOLDER_FLOOR && repeated(o.source, o.from)) {
        o.placeholder = true;
        by.repeat++;
      }
    }
    for (const p of offers.filter((o) => o.placeholder)) {
      for (const o of offers) {
        if (!o.placeholder && o.from >= PLACEHOLDER_FLOOR && twin(p, o)) {
          o.placeholder = true;
          by.twin++;
        }
      }
    }
    const priced = offers.filter((o) => o.from > 0);
    if (priced.length === 1 && !priced[0].placeholder && priced[0].from >= PLACEHOLDER_FLOOR && e.artist) {
      const others = (byAct.get(e.artist) ?? []).filter((x) => x.i !== i).map((x) => x.price);
      if (others.length >= 3 && priced[0].from >= PLACEHOLDER_MULT * median(others)) {
        priced[0].placeholder = true;
        by.outlier++;
      }
    }
  });
  return by;
}

/**
 * How often each stand-in-sized price appears in each marketplace's whole feed: `source|cents` ->
 * { events, venues } for the rows priced at least PLACEHOLDER_FLOOR. A build matches only a few of the
 * 334 events that carry $981, so markPlaceholders takes these counts from the full Ticket Squeeze and
 * TicketNetwork pulls, per marketplace.
 * @param {Record<string, { min?: number | null, venue?: string, artist?: string, name?: string }[]>} bySource feed rows by marketplace name
 */
export const placeholderCounts = (bySource) => {
  /** @type {Map<string, { events: number, venues: Set<string>, acts: Set<string> }>} */
  const acc = new Map();
  for (const [source, rows] of Object.entries(bySource))
    for (const r of rows ?? []) {
      if (!(typeof r.min === 'number' && r.min >= PLACEHOLDER_FLOOR)) continue;
      const k = `${source}|${Math.round(r.min * 100)}`;
      const b = acc.get(k) ?? { events: 0, venues: new Set(), acts: new Set() };
      b.events++;
      b.venues.add(String(r.venue ?? ''));
      const act = String(/** @type {any} */ (r).artist || /** @type {any} */ (r).name || '').toLowerCase();
      if (act) b.acts.add(act);
      acc.set(k, b);
    }
  const out = new Map([...acc].map(([k, b]) => [k, { events: b.events, venues: b.venues.size, ...(b.acts.size ? { acts: b.acts.size } : {}) }]));
  // THE STAND-IN FAMILY. A feed minimum that repeats ($999.14 on 443 events at 166 venues) reaches the
  // pages as the widget readings of the same listings: Ticket Squeeze's 999.14 x 1.22 = $1,219 (whole
  // dollars) and TicketNetwork's $1,219 / $1,386.90. The widget passes run BEFORE the placeholders are
  // marked, so the counts must know those values too: each Ticket Squeeze price that repeats enough also
  // seeds its x1.22 value for both exchange marketplaces with the same counts (the 1.138 twin rule then
  // catches TicketNetwork's $1,386.90 from the flagged Ticket Squeeze $1,219).
  for (const [k, c] of [...out]) {
    const [source, cents] = k.split('|');
    if (source !== 'Ticket Squeeze' || c.events < PLACEHOLDER_REPEAT || c.venues < PLACEHOLDER_VENUES) continue;
    const family = Math.round((Number(cents) / 100) * 1.22) * 100;
    for (const s of ['Ticket Squeeze', 'TicketNetwork']) {
      const fk = `${s}|${family}`;
      const have = out.get(fk);
      if (!have || have.events < c.events) out.set(fk, { events: Math.max(c.events, have?.events ?? 0), venues: Math.max(c.venues, have?.venues ?? 0), ...(c.acts || have?.acts ? { acts: Math.max(c.acts ?? 0, have?.acts ?? 0) } : {}) });
    }
  }
  return out;
};

/**
 * BOARD ORDER AND CLAIMS (2026-10-19). The price board, the mobile buy bar, the event FAQ and the
 * product demo each sorted the offers and named a winner their own way, and contradicted each
 * other: "LOWEST LISTED" above a lower number on 9.7% of boards, "LOWEST" on 30,829 boards that
 * compared nothing, an FAQ calling a site "the cheapest right now" after listing a lower price.
 * `boardOrder` (above) and `boardClaim` are the one rule all of them use. TicketNetwork's affiliate
 * terms forbid "cheapest"/"lowest" claims we cannot back, so a claim is made only when the data
 * backs it.
 */

/**
 * What an offer costs with fees, as far as we know: its own price when fees are included, else
 * its sticker price times its marketplace's measured fee wedge (rankPrice). An ESTIMATE: it ranks
 * the board, and is said as one ("about $45 with its usual fees"), never shown as a price.
 * @param {{ source?: string, allIn?: boolean, from: number }} offer
 */
export const estTotal = (offer) => rankPrice(offer);

/**
 * Estimated totals on different fee bases this close are a tie, not a win: a sticker price's
 * estimate uses its marketplace's MEDIAN fee wedge, not that listing's checkout (1,481 boards had
 * their top two within 1%, 2026-10-18).
 */
export const CLAIM_TIE = 0.01;

/** a price in whole cents, so 75 × 1.08 = 81.00000000000001 ties with an $81.00 price */
const cents = (/** @type {number} */ n) => Math.round(n * 100);

/**
 * The claim the board may make about its cheapest row(s), or null when it may make none:
 *  - null when any priced offer is suspect (the board itself is in doubt);
 *  - only offers a headline may quote take part (usableOffer), and only those whose floor is not a
 *    single seat (qty 1): the board's default is two tickets, and "Lowest" about a price nobody can
 *    pay for two is a claim the data does not back. Fewer than two such offers: null;
 *  - null when an exchange offer without a widget reading today (held, see rankInner) is cheaper
 *    than the winner: its feed minimum is probably a single seat, so the board is in doubt;
 *  - 'Lowest' when every price includes fees: the row(s) with the lowest price (ties: all);
 *  - 'Lowest est. total' when some prices are before fees: the row(s) with the lowest estimated
 *    total — never a sticker row whose bare number merely ties or undercuts it — or null when
 *    another offer comes within CLAIM_TIE of that total and either of the two is an estimate
 *    (a different fee basis, or another sticker price).
 * Always on estimated totals (rankPrice), never on raw `from`. `sources` are offer.source names.
 * @param {RankedOffer[] | null | undefined} offers
 * @returns {{ sources: string[], label: 'Lowest' | 'Lowest est. total' } | null}
 */
export const boardClaim = (offers) => {
  const all = (offers ?? []).filter((o) => o.from > 0);
  if (all.some((o) => o.suspect)) return null;
  const { held } = rankInner(all);
  const priced = all.filter((o) => usableOffer(o) && !(PRICE_FLAGS.pairFloors && o.qty === 1) && !held.has(o));
  if (priced.length < 2) return null;
  const est = priced.map((o) => ({ source: String(o.source ?? ''), total: cents(rankPrice(o)), allIn: isAllIn(o) }));
  const top = Math.min(...est.map((e) => e.total));
  if ([...held].some((o) => cents(rankPrice(o)) < top)) return null;
  const winners = est.filter((e) => e.total === top);
  if (est.every((e) => e.allIn)) return { sources: winners.map((e) => e.source), label: 'Lowest' };
  const first = winners[0];
  const tooClose = est.some((e) => e !== first && (e.allIn !== first.allIn || !e.allIn) && e.total <= top * (1 + CLAIM_TIE));
  return tooClose ? null : { sources: winners.map((e) => e.source), label: 'Lowest est. total' };
};

/**
 * Offers with their fee basis written down: every consumer then reads the same answer,
 * whether or not it knows the providers' rules. Mutates and returns the list.
 * @template {{ source?: string, allIn?: boolean }} O
 * @param {O[] | null | undefined} offers
 */
export const withFeeBasis = (offers) => {
  for (const o of offers ?? []) if (typeof o.allIn !== 'boolean') o.allIn = isAllIn(o);
  return offers;
};

/**
 * D1 keeps each partner link once, in its offer: drop an event's link fields that repeat their
 * offer's URL (the build, before the push). `expandLinks` puts them back (the edge, before
 * rendering). Mutates and returns the doc.
 * @template {Record<string, any>} D
 * @param {D} doc
 */
export const compactLinks = (doc) => {
  for (const p of PROVIDERS) {
    const url = (doc.offers ?? []).find((/** @type {any} */ o) => o.source === p.name)?.url;
    if (url && doc[p.field] === url) delete doc[p.field];
  }
  return doc;
};

/**
 * @template {Record<string, any>} D
 * @param {D} doc
 */
export const expandLinks = (doc) => {
  for (const o of doc.offers ?? []) {
    const p = providerByName(o.source);
    if (p && o.url && !doc[p.field]) /** @type {any} */ (doc)[p.field] = o.url;
  }
  return doc;
};

// ---- PRICE HISTORY ON ONE COMPARABLE BASIS (clean-price-history, 2026-10-22) -----------------
//
// Every history number (trend, "down today", lowest seen, 30-day low/median/range, 7-day move, the
// FAQ, the homepage drops, the research) reads the same daily floors: the cheapest COMPARABLE,
// UNFLAGGED price of the day. A recorded point is [date, price, allIn?, qty?]: allIn 1 = fees
// included, 0 = before fees (written only when it differs from the marketplace's rule), qty 2 = a
// price for two or more tickets (the widget readings from 2026-10-22 on; absent = the marketplace's
// own minimum, often one seat).

/** Ticket Squeeze readings carry their own fee basis from this day on (scripts/gen-catalog.mjs `point`) */
export const TS_BASIS_FROM = '2026-10-14';
/** D1's price_point.all_in is each point's own fee basis only from this day on: until 2026-10-14
 *  every point went in as 0 (scripts/db-push.mjs), so earlier rows follow their marketplace's rule. */
export const D1_BASIS_FROM = '2026-10-15';

const isFlag = (/** @type {unknown} */ x) => x === 0 || x === 1;

/**
 * Does this recorded point include fees? Its own slot when it has one; else, for a Ticket Squeeze
 * reading from before TS_BASIS_FROM (when the basis was not recorded), the shape of the number — a
 * widget reading is always whole dollars (14,932 of 14,932), a feed price 3.3% of the time, and 86%
 * of those 109,271 old points are whole dollars — else the marketplace's rule.
 * @param {readonly unknown[]} point [date, price, allIn?, qty?]
 * @param {string} source
 */
export const pointAllIn = (point, source) => {
  if (isFlag(point[2])) return point[2] === 1;
  if (source === 'Ticket Squeeze' && String(point[0]) < TS_BASIS_FROM) return Number.isInteger(point[1]);
  return isAllIn({ source });
};

/** Is that basis a record (a slot, or a marketplace whose rule never changed) rather than a guess
 *  from the number's shape? @param {readonly unknown[]} point @param {string} source */
export const pointBasisKnown = (point, source) => isFlag(point[2]) || source !== 'Ticket Squeeze' || String(point[0]) >= TS_BASIS_FROM;

/** @param {number} n */
const round2 = (n) => Math.round(n * 100) / 100;

/**
 * The point's price with fees: itself when fees are included, else the sticker price times its
 * marketplace's fee wedge — an ESTIMATE, which every number made from it says (`est`).
 * @param {readonly unknown[]} point @param {string} source
 */
export const comparablePrice = (point, source) => (pointAllIn(point, source) ? Number(point[1]) : round2(Number(point[1]) * feeWedge(source)));

/** What quantity a point's price is for: 2 = two or more tickets, 1 = unknown or the marketplace's minimum. @param {readonly unknown[]} point */
export const pointQty = (point) => (point[3] === 2 ? 2 : 1);

/**
 * One recorded day's phantom prices: the board's own markSuspects across that day's points from
 * every marketplace (a before-fees price under half the cheapest fees-included one), plus the old
 * Ticket Squeeze points whose basis is only a guess: those are also tested against the cheapest
 * fees-included reading whose basis is KNOWN. A fees-included reading with a known basis is never
 * flagged (HelloTickets $30 at 49ers-Falcons can be real). Flagged points stay in the day's table,
 * marked, and leave every statistic.
 * @param {{ source: string, price: number, allIn: boolean, known: boolean }[]} pts
 * @returns {boolean[]} parallel to `pts`
 */
export const dayFlags = (pts) => {
  // a stand-in reading (`standIn`: a known broker price, see standInKeys) is flagged outright and is no
  // reference for anything else
  const offers = pts.map((p) => ({ source: p.source, allIn: p.allIn, from: p.price, ...(p.standIn ? { placeholder: true } : {}) }));
  markSuspects(offers);
  const known = pts.filter((p) => !p.standIn && p.allIn && p.known && p.price > 0).map((p) => p.price);
  const ref = known.length ? Math.min(...known) : null;
  return pts.map((p, i) => !!p.standIn || !!/** @type {any} */ (offers[i]).suspect || (!p.known && ref !== null && p.price < ref * SUSPECT_RATIO));
};

/**
 * The stand-in prices of an event as history keys (`source|cents`): its flagged offers' prices. Recorded
 * days at exactly these prices from the same marketplace are the same stand-in, and leave every
 * statistic like a phantom (marked unconfirmed in the table; the history itself is never edited).
 * @param {{ source?: string, from: number, placeholder?: boolean }[] | null | undefined} offers
 * @returns {Set<string>}
 */
export const standInKeys = (offers) => new Set((offers ?? []).filter((o) => o.placeholder).map((o) => `${o.source ?? ''}|${Math.round(o.from * 100)}`));

/**
 * The stand-in prices every event shares: the keys of placeholderCounts that reach the repeat rule
 * (including the widget family), as a set of `source|cents` for the history (clean-price-history).
 * @param {Map<string, { events: number, venues: number }>} counts
 * @returns {Set<string>}
 */
export const standInSet = (counts) => new Set([...counts].filter(([, c]) => c.events >= PLACEHOLDER_REPEAT && c.venues >= PLACEHOLDER_VENUES).map(([k]) => k));

/**
 * A D1 price_point row as a recorded point. D1 stored all_in = 0 for every marketplace until
 * 2026-10-14 (HelloTickets and TicketNetwork until about the 15th), so a 0 there says nothing: fees
 * are included when the column says 1 OR the marketplace's own rule says so, and the basis goes
 * into the point only where it differs from that rule. A Ticket Squeeze row of 2026-10-14 (the
 * file tape knew its basis, D1 did not yet) is read by the shape of its number, as pointAllIn does
 * for the days before it. Reading the column raw would multiply real HelloTickets and TicketNetwork
 * prices by the fee wedge on every edge page.
 * @param {string} source @param {string} observed @param {number} price @param {number | null | undefined} allIn
 * @returns {[string, number] | [string, number, 0 | 1]}
 */
export const d1Point = (source, observed, price, allIn) => {
  const rule = isAllIn({ source });
  if (allIn === 1) return rule ? [observed, price] : [observed, price, 1];
  if (source === 'Ticket Squeeze' && observed >= TS_BASIS_FROM && observed < D1_BASIS_FROM && Number.isInteger(price)) return [observed, price, 1];
  return [observed, price];
};
